/**
 * ShotDetailChart Component
 *
 * Renders a D3-based hexbin shot chart visualization.
 * Uses the useHexbinChart hook for D3 logic and animations.
 * Supports hexbin selection with persistent highlighting.
 */

import { Box, Typography, useMediaQuery, Theme } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import {
  ShotDetailChartProps,
  HexbinData,
  getVisibleCourtLength,
  COURT_CONSTANTS,
} from "./types";
import { useHexbinChart } from "./useHexbinChart";
import { formatTooltipText } from "./utils";

// Default chart dimensions - larger to fill available space
const DEFAULT_WIDTH = 520;
const MOBILE_WIDTH = 300;

export default function ShotDetailChart({
  shots,
  filters,
  width: propWidth,
  height: propHeight,
  onHexagonHover,
  onHexagonSelect,
  selectedHexKey,
  showCollegeLine = false,
  onHexbinDataChange,
  bgcolor = "#f8f8f8",
}: ShotDetailChartProps) {
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  // Calculate dimensions
  const width = propWidth ?? (isMobile ? MOBILE_WIDTH : DEFAULT_WIDTH);
  const aspectRatio = getVisibleCourtLength() / COURT_CONSTANTS.COURT_WIDTH;
  const height = propHeight ?? width * aspectRatio;

  // Tooltip state
  const [tooltipData, setTooltipData] = useState<{
    hexbin: HexbinData;
    x: number;
    y: number;
  } | null>(null);

  // Handle hexagon hover
  const handleHexagonHover = useCallback(
    (data: HexbinData | null, event: MouseEvent) => {
      if (data) {
        // Get position relative to chart container
        const target = event.currentTarget as SVGElement;
        const rect = target.ownerSVGElement?.getBoundingClientRect();
        if (rect) {
          setTooltipData({
            hexbin: data,
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          });
        }
      } else {
        setTooltipData(null);
      }

      // Call parent handler if provided
      onHexagonHover?.(data, event);
    },
    [onHexagonHover],
  );

  // Handle hexagon click for selection
  const handleHexagonClick = useCallback(
    (data: HexbinData) => {
      // Toggle selection - if clicking same hex, deselect
      if (selectedHexKey === data.key) {
        onHexagonSelect?.(null);
      } else {
        onHexagonSelect?.(data);
      }
    },
    [selectedHexKey, onHexagonSelect],
  );

  // Use the hexbin chart hook
  const { svgRef, filteredShotCount, hexbinData } = useHexbinChart(
    shots,
    filters,
    {
      width,
      height,
      showCollegeLine,
      onHexagonHover: handleHexagonHover,
      onHexagonClick: handleHexagonClick,
      selectedHexKey,
    },
  );

  // Notify parent when hexbinData changes (for SelectedHexbinStats)
  useEffect(() => {
    // Always notify parent of hexbin data changes
    onHexbinDataChange?.(hexbinData);

    if (selectedHexKey && onHexagonSelect) {
      // Find the updated hexbin data for the selected key
      const updatedHexbin = hexbinData.find((h) => h.key === selectedHexKey);
      // If the selected hex no longer exists in data, keep selection but with null data
      // The parent will handle showing empty state
      if (!updatedHexbin) {
        // Selected hex no longer has any shots matching filters
        // We pass a special "empty" hexbin to indicate this
        onHexagonSelect({
          key: selectedHexKey,
          x: 0,
          y: 0,
          shots: [],
          makes: 0,
          attempts: 0,
          ptsScored: 0,
          fgPct: 0,
          tsPct: 0,
          mergedAttempts: 0,
          mergedMakes: 0,
          mergedPtsScored: 0,
          mergedTsPct: 0,
          neighborKeys: [],
        });
      }
    }
  }, [hexbinData, selectedHexKey, onHexagonSelect, onHexbinDataChange]);

  return (
    <Box
      sx={{
        position: "relative",
        width: width,
        height: height,
        bgcolor,
        borderRadius: 1,
        overflow: "visible", // Allow tooltip to overflow at edges
      }}
    >
      {/* SVG Chart */}
      <svg
        ref={svgRef}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
        }}
      />

      {/* Tooltip */}
      {tooltipData && (
        <Box
          sx={{
            position: "absolute",
            left: tooltipData.x,
            top: tooltipData.y,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(50, 50, 50, 0.7)", // More transparent, lighter
              color: "white",
              px: 1,
              py: 0.5,
              borderRadius: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              whiteSpace: "nowrap",
              boxShadow: 1,
              mb: 0.5,
            }}
          >
            {formatTooltipText(tooltipData.hexbin)}
          </Box>
        </Box>
      )}

      {/* Empty state */}
      {shots.length > 0 && filteredShotCount === 0 && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            p: 3,
          }}
        >
          <Typography color="text.secondary">
            No shots match the current filters.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try adjusting your filter selections.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
